import { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  getCostAnalytics,
  getLayoutAnalytics,
  getPerformanceAnalytics,
  getQualityAnalytics,
  type CostAnalytics,
  type LayoutAnalytics,
  type PerformanceAnalytics,
  type QualityAnalytics,
} from "../../client";

type AnalyticsDashboardProps = {
  jobId?: string;
};

type TabType = "costs" | "performance" | "quality" | "layout";

const COLORS = ["#4a90d9", "#50c878", "#f5a623", "#e74c3c", "#9b59b6", "#3498db"];

export function AnalyticsDashboard({ jobId }: AnalyticsDashboardProps) {
  const [activeTab, setActiveTab] = useState<TabType>("costs");
  const [costData, setCostData] = useState<CostAnalytics | null>(null);
  const [perfData, setPerfData] = useState<PerformanceAnalytics | null>(null);
  const [qualityData, setQualityData] = useState<QualityAnalytics | null>(null);
  const [layoutData, setLayoutData] = useState<LayoutAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [costs, perf, quality, layout] = await Promise.all([
        getCostAnalytics(),
        getPerformanceAnalytics(),
        getQualityAnalytics(),
        getLayoutAnalytics(),
      ]);
      setCostData(costs);
      setPerfData(perf);
      setQualityData(quality);
      setLayoutData(layout);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const tabs: { id: TabType; label: string }[] = [
    { id: "costs", label: "Costs" },
    { id: "performance", label: "Performance" },
    { id: "quality", label: "Quality" },
    { id: "layout", label: "Layout" },
  ];

  if (loading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", color: "#888" }}>
        Loading analytics...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", color: "#e74c3c" }}>
        {error}
        <br />
        <button
          onClick={loadData}
          style={{
            marginTop: "1rem",
            padding: "0.5rem 1rem",
            background: "#4a90d9",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ color: "#e0e0e0" }}>
      <style>{`
        .analytics-grid-2 {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
          margin-bottom: 2rem;
        }
        .analytics-grid-3 {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
          margin-bottom: 2rem;
        }
        .analytics-grid-4 {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
          margin-bottom: 2rem;
        }
        .analytics-tabs {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
        }
        .analytics-table-wrapper {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        @media (min-width: 640px) {
          .analytics-grid-2 {
            grid-template-columns: repeat(2, 1fr);
          }
          .analytics-grid-3 {
            grid-template-columns: repeat(3, 1fr);
          }
          .analytics-grid-4 {
            grid-template-columns: repeat(4, 1fr);
          }
        }
      `}</style>
      {/* Sub-tabs */}
      <div
        style={{
          display: "flex",
          gap: "0.5rem",
          marginBottom: "1.5rem",
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "0.5rem 1rem",
              background: activeTab === tab.id ? "#4a90d9" : "#2a2a2a",
              border: "none",
              borderRadius: "4px",
              color: activeTab === tab.id ? "white" : "#888",
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: activeTab === tab.id ? 600 : 400,
              transition: "all 0.15s ease",
            }}
          >
            {tab.label}
          </button>
        ))}
        <button
          onClick={loadData}
          style={{
            marginLeft: "auto",
            padding: "0.5rem 1rem",
            background: "#333",
            border: "none",
            borderRadius: "4px",
            color: "#888",
            cursor: "pointer",
            fontSize: "0.75rem",
          }}
        >
          Refresh
        </button>
      </div>

      {activeTab === "costs" && <CostsTab data={costData} />}
      {activeTab === "performance" && <PerformanceTab data={perfData} />}
      {activeTab === "quality" && <QualityTab data={qualityData} />}
      {activeTab === "layout" && <LayoutTab data={layoutData} />}
    </div>
  );
}

function CostsTab({ data }: { data: CostAnalytics | null }) {
  if (!data || data.total_tokens === 0) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", color: "#666" }}>
        No cost data available yet. Run some generation jobs to see analytics.
      </div>
    );
  }

  return (
    <div>
      {/* Summary Cards */}
      <div className="analytics-grid-4">
        <SummaryCard
          label="Total Tokens"
          value={formatNumber(data.total_tokens)}
        />
        <SummaryCard
          label="Input Tokens"
          value={formatNumber(data.total_input_tokens)}
        />
        <SummaryCard
          label="Output Tokens"
          value={formatNumber(data.total_output_tokens)}
        />
        <SummaryCard
          label="Estimated Cost"
          value={`$${data.estimated_cost_usd.toFixed(4)}`}
          highlight
        />
      </div>

      {/* Cost Chart */}
      {data.breakdown.length > 0 && (
        <div style={{ marginBottom: "2rem" }}>
          <h3 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: "1rem" }}>
            Token Usage by Day
          </h3>
          <div style={{ height: "300px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.breakdown.slice(-14).reverse()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="label" tick={{ fill: "#888", fontSize: 12 }} />
                <YAxis tick={{ fill: "#888", fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ background: "#1a1a1a", border: "1px solid #333" }}
                  labelStyle={{ color: "#e0e0e0" }}
                />
                <Bar dataKey="input_tokens" name="Input" fill="#4a90d9" stackId="a" />
                <Bar dataKey="output_tokens" name="Output" fill="#50c878" stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

function PerformanceTab({ data }: { data: PerformanceAnalytics | null }) {
  if (!data || data.stage_breakdown.length === 0) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", color: "#666" }}>
        No performance data available yet. Run some generation jobs to see analytics.
      </div>
    );
  }

  return (
    <div>
      {/* Summary Cards */}
      <div className="analytics-grid-3">
        <SummaryCard
          label="Avg Duration"
          value={formatDuration(data.avg_total_duration_sec)}
        />
        <SummaryCard
          label="P50 Duration"
          value={formatDuration(data.p50_duration_sec)}
        />
        <SummaryCard
          label="P95 Duration"
          value={formatDuration(data.p95_duration_sec)}
        />
      </div>

      {/* Stage Duration Chart */}
      <div style={{ marginBottom: "2rem" }}>
        <h3 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: "1rem" }}>
          Average Duration by Stage
        </h3>
        <div style={{ height: "300px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.stage_breakdown} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis type="number" tick={{ fill: "#888", fontSize: 12 }} />
              <YAxis
                type="category"
                dataKey="stage"
                tick={{ fill: "#888", fontSize: 12 }}
                width={80}
              />
              <Tooltip
                contentStyle={{ background: "#1a1a1a", border: "1px solid #333" }}
                labelStyle={{ color: "#e0e0e0" }}
                formatter={(value: number) => formatDuration(value)}
              />
              <Bar dataKey="avg_duration_sec" name="Avg Duration" fill="#4a90d9" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Stage Details Table */}
      <div>
        <h3 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: "1rem" }}>
          Stage Details
        </h3>
        <div className="analytics-table-wrapper">
        <table
          style={{
            width: "100%",
            minWidth: "500px",
            borderCollapse: "collapse",
            fontSize: "0.875rem",
          }}
        >
          <thead>
            <tr style={{ borderBottom: "1px solid #333" }}>
              <th style={{ textAlign: "left", padding: "0.5rem", color: "#888" }}>Stage</th>
              <th style={{ textAlign: "right", padding: "0.5rem", color: "#888" }}>Avg</th>
              <th style={{ textAlign: "right", padding: "0.5rem", color: "#888" }}>Min</th>
              <th style={{ textAlign: "right", padding: "0.5rem", color: "#888" }}>Max</th>
              <th style={{ textAlign: "right", padding: "0.5rem", color: "#888" }}>P50</th>
              <th style={{ textAlign: "right", padding: "0.5rem", color: "#888" }}>P95</th>
              <th style={{ textAlign: "right", padding: "0.5rem", color: "#888" }}>Samples</th>
            </tr>
          </thead>
          <tbody>
            {data.stage_breakdown.map((stage) => (
              <tr key={stage.stage} style={{ borderBottom: "1px solid #222" }}>
                <td style={{ padding: "0.5rem" }}>{stage.stage}</td>
                <td style={{ textAlign: "right", padding: "0.5rem" }}>{formatDuration(stage.avg_duration_sec)}</td>
                <td style={{ textAlign: "right", padding: "0.5rem" }}>{formatDuration(stage.min_duration_sec)}</td>
                <td style={{ textAlign: "right", padding: "0.5rem" }}>{formatDuration(stage.max_duration_sec)}</td>
                <td style={{ textAlign: "right", padding: "0.5rem" }}>{formatDuration(stage.p50_duration_sec)}</td>
                <td style={{ textAlign: "right", padding: "0.5rem" }}>{formatDuration(stage.p95_duration_sec)}</td>
                <td style={{ textAlign: "right", padding: "0.5rem" }}>{stage.sample_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

function QualityTab({ data }: { data: QualityAnalytics | null }) {
  if (!data || data.total_jobs === 0) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", color: "#666" }}>
        No quality data available yet. Run some generation jobs to see analytics.
      </div>
    );
  }

  const pieData = [
    { name: "Successful", value: data.successful_jobs },
    { name: "Failed", value: data.failed_jobs },
  ].filter((d) => d.value > 0);

  return (
    <div>
      {/* Summary Cards */}
      <div className="analytics-grid-4">
        <SummaryCard label="Total Jobs" value={data.total_jobs} />
        <SummaryCard
          label="Success Rate"
          value={`${(data.success_rate * 100).toFixed(1)}%`}
          highlight={data.success_rate > 0.9}
        />
        <SummaryCard
          label="Avg Retries"
          value={data.avg_retry_count.toFixed(2)}
        />
        <SummaryCard
          label="Cache Hit Rate"
          value={`${(data.cache_hit_rate * 100).toFixed(1)}%`}
        />
      </div>

      {/* Success/Failure Pie Chart */}
      {pieData.length > 0 && (
        <div className="analytics-grid-2">
          <div>
            <h3 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: "1rem" }}>
              Job Outcomes
            </h3>
            <div style={{ height: "250px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label
                  >
                    {pieData.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={index === 0 ? "#50c878" : "#e74c3c"}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "#1a1a1a", border: "1px solid #333" }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Error Breakdown */}
          {data.error_breakdown.length > 0 && (
            <div>
              <h3 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: "1rem" }}>
                Errors by Stage
              </h3>
              <div style={{ height: "250px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.error_breakdown} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis type="number" tick={{ fill: "#888", fontSize: 12 }} />
                    <YAxis
                      type="category"
                      dataKey="stage"
                      tick={{ fill: "#888", fontSize: 12 }}
                      width={80}
                    />
                    <Tooltip
                      contentStyle={{ background: "#1a1a1a", border: "1px solid #333" }}
                    />
                    <Bar dataKey="error_count" name="Errors" fill="#e74c3c" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LayoutTab({ data }: { data: LayoutAnalytics | null }) {
  if (!data || data.total_scenes === 0) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", color: "#666" }}>
        No layout data available yet. Run some generation jobs to see layout analytics.
      </div>
    );
  }

  // Calculate layout grade based on score
  const getGrade = (score: number): { grade: string; color: string } => {
    if (score >= 90) return { grade: "A", color: "#50c878" };
    if (score >= 80) return { grade: "B", color: "#8bc34a" };
    if (score >= 70) return { grade: "C", color: "#f5a623" };
    if (score >= 60) return { grade: "D", color: "#ff9800" };
    return { grade: "F", color: "#e74c3c" };
  };

  const { grade, color: gradeColor } = getGrade(data.avg_layout_score || 0);

  // Prepare pie data for issue breakdown
  const issueBreakdown = [
    { name: "Collisions", value: data.total_collisions },
    { name: "Out of Frame", value: data.total_out_of_frame },
    { name: "Timing Issues", value: data.total_timing_warnings },
  ].filter((d) => d.value > 0);

  return (
    <div>
      {/* Summary Cards */}
      <div className="analytics-grid-4">
        <SummaryCard label="Total Scenes" value={data.total_scenes} />
        <SummaryCard
          label="Clean Rate"
          value={`${(data.clean_rate * 100).toFixed(1)}%`}
          highlight={data.clean_rate > 0.8}
        />
        <SummaryCard label="Total Collisions" value={data.total_collisions} />
        <div
          style={{
            background: "#1a1a1a",
            padding: "1rem",
            borderRadius: "8px",
            border: `2px solid ${gradeColor}`,
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "0.75rem", color: "#888", marginBottom: "0.25rem" }}>
            Layout Score
          </div>
          <div
            style={{
              fontSize: "2rem",
              fontWeight: 700,
              color: gradeColor,
            }}
          >
            {grade}
          </div>
          <div style={{ fontSize: "0.875rem", color: "#888" }}>
            {(data.avg_layout_score || 0).toFixed(1)}/100
          </div>
        </div>
      </div>

      {/* Issue Breakdown and Daily Trend */}
      <div className="analytics-grid-2">
        {/* Issue Distribution Pie Chart */}
        {issueBreakdown.length > 0 && (
          <div>
            <h3 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: "1rem" }}>
              Issue Distribution
            </h3>
            <div style={{ height: "250px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={issueBreakdown}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {issueBreakdown.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "#1a1a1a", border: "1px solid #333" }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Additional Stats */}
        <div>
          <h3 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: "1rem" }}>
            Issue Summary
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            <IssueRow
              label="Element Collisions"
              count={data.total_collisions}
              color="#e74c3c"
              description="Elements overlapping each other"
            />
            <IssueRow
              label="Out of Frame"
              count={data.total_out_of_frame}
              color="#f5a623"
              description="Elements extending beyond canvas"
            />
            <IssueRow
              label="Timing Warnings"
              count={data.total_timing_warnings}
              color="#9b59b6"
              description="Elements displayed too briefly"
            />
            <div
              style={{
                marginTop: "0.5rem",
                padding: "0.75rem",
                background: "#0d2818",
                borderRadius: "6px",
                border: "1px solid #50c878",
              }}
            >
              <div style={{ fontSize: "0.875rem", color: "#50c878" }}>
                {data.clean_scenes} of {data.total_scenes} scenes are clean (no issues)
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Issues Chart */}
      {data.daily_breakdown.length > 0 && (
        <div style={{ marginTop: "2rem" }}>
          <h3 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: "1rem" }}>
            Issues by Day
          </h3>
          <div style={{ height: "300px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.daily_breakdown.slice(-14).reverse()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="date" tick={{ fill: "#888", fontSize: 12 }} />
                <YAxis tick={{ fill: "#888", fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ background: "#1a1a1a", border: "1px solid #333" }}
                  labelStyle={{ color: "#e0e0e0" }}
                />
                <Bar dataKey="collisions" name="Collisions" fill="#e74c3c" stackId="a" />
                <Bar dataKey="out_of_frame" name="Out of Frame" fill="#f5a623" stackId="a" />
                <Bar dataKey="timing_warnings" name="Timing" fill="#9b59b6" stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Scene Breakdown Table */}
      {data.scene_breakdown.length > 0 && (
        <div style={{ marginTop: "2rem" }}>
          <h3 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: "1rem" }}>
            Issues by Scene Type
          </h3>
          <div className="analytics-table-wrapper">
            <table
              style={{
                width: "100%",
                minWidth: "500px",
                borderCollapse: "collapse",
                fontSize: "0.875rem",
              }}
            >
              <thead>
                <tr style={{ borderBottom: "1px solid #333" }}>
                  <th style={{ textAlign: "left", padding: "0.5rem", color: "#888" }}>Scene</th>
                  <th style={{ textAlign: "right", padding: "0.5rem", color: "#888" }}>Count</th>
                  <th style={{ textAlign: "right", padding: "0.5rem", color: "#888" }}>Collisions</th>
                  <th style={{ textAlign: "right", padding: "0.5rem", color: "#888" }}>Out of Frame</th>
                  <th style={{ textAlign: "right", padding: "0.5rem", color: "#888" }}>Timing</th>
                  <th style={{ textAlign: "right", padding: "0.5rem", color: "#888" }}>Avg Score</th>
                </tr>
              </thead>
              <tbody>
                {data.scene_breakdown.map((scene) => {
                  const sceneGrade = getGrade(scene.avg_score || 0);
                  return (
                    <tr key={scene.scene} style={{ borderBottom: "1px solid #222" }}>
                      <td style={{ padding: "0.5rem" }}>{scene.scene}</td>
                      <td style={{ textAlign: "right", padding: "0.5rem" }}>{scene.occurrence_count}</td>
                      <td style={{ textAlign: "right", padding: "0.5rem", color: scene.collisions > 0 ? "#e74c3c" : "#888" }}>
                        {scene.collisions}
                      </td>
                      <td style={{ textAlign: "right", padding: "0.5rem", color: scene.out_of_frame > 0 ? "#f5a623" : "#888" }}>
                        {scene.out_of_frame}
                      </td>
                      <td style={{ textAlign: "right", padding: "0.5rem", color: scene.timing_warnings > 0 ? "#9b59b6" : "#888" }}>
                        {scene.timing_warnings}
                      </td>
                      <td style={{ textAlign: "right", padding: "0.5rem", color: sceneGrade.color }}>
                        {(scene.avg_score || 0).toFixed(1)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function IssueRow({
  label,
  count,
  color,
  description,
}: {
  label: string;
  count: number;
  color: string;
  description: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0.75rem",
        background: "#1a1a1a",
        borderRadius: "6px",
        borderLeft: `3px solid ${color}`,
      }}
    >
      <div>
        <div style={{ fontSize: "0.875rem", fontWeight: 500 }}>{label}</div>
        <div style={{ fontSize: "0.75rem", color: "#666" }}>{description}</div>
      </div>
      <div
        style={{
          fontSize: "1.25rem",
          fontWeight: 600,
          color: count > 0 ? color : "#666",
        }}
      >
        {count}
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: string | number;
  highlight?: boolean;
}) {
  return (
    <div
      style={{
        background: highlight ? "#2a3a4a" : "#1a1a1a",
        padding: "1rem",
        borderRadius: "8px",
        border: highlight ? "1px solid #4a90d9" : "1px solid #333",
      }}
    >
      <div style={{ fontSize: "0.75rem", color: "#888", marginBottom: "0.5rem" }}>
        {label}
      </div>
      <div
        style={{
          fontSize: "1.5rem",
          fontWeight: 600,
          color: highlight ? "#4a90d9" : "#e0e0e0",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs.toFixed(0)}s`;
}
